import { Button, Flex, Layout, Modal, type TableColumnsType, theme, Typography } from 'antd';
import { type SortOrder } from 'antd/es/table/interface';
import { CircleAlert, CircleCheck, CircleX, PlusCircle, Trash2 } from 'lucide-react';
import PropTypes from 'prop-types';
import React, {
  type Key,
  type ReactNode,
  Suspense,
  useContext,
  useEffect,
  useReducer,
  useState,
} from 'react';
import { from, switchMap, throwError, of, tap, catchError, EMPTY } from 'rxjs';
import CustomerFormTabsComponent from './customer-form-tabs.component';
import CustomersFilter, { type CustomersFiltersProps } from './customers.filter';
import AuthorizedComponent from '../../../components/authorized-component';
import { DataTable } from '../../../components/data-table';
import LoadingSpin from '../../../components/loading-spin';
import SyncSuspense from '../../../components/sync-suspense';
import { AuthContext } from '../../../contexts/auth-context-provider';
import { ExtraContent } from '../../../contexts/extra-content-context-provider';
import { Customer } from '../../../entities';
import useDebouncedState from '../../../hooks/use-debounced-state';
import { useNotification } from '../../../hooks/use-notification';
import { useQuery } from '../../../jsonapi-provider';
import { Roles } from '../../../types';

interface customersPageState {
  filters: CustomersFiltersProps['initialFilters'];
  pagination: { current: number; pageSize: number };

  sort: { order?: SortOrder; field?: Key | readonly Key[] };
}

const DEFAULT_PAGE_SIZE = 10;
const DEFAULT_PAGE_NUMBER = 1;
const DEFAULT_SORT_FIELD = 'name';
const DEFAULT_SORT_DIRECTION: SortOrder = 'ascend';
const SUCCESS_DELETE_MESSAGE = 'Customer deleted successfully';

const reducer = (
  state: customersPageState,
  action: { type: 'RESET_FILTER' | 'SET_FILTER' | 'SET_PAGINATION' | 'SET_SORT'; payload?: any }
): customersPageState => {
  switch (action.type) {
    case 'RESET_FILTER':
      return { ...state, filters: { query: '', showInActive: false } };
    case 'SET_FILTER':
      return {
        ...state,
        filters: action.payload,
        pagination: {
          ...state?.pagination,
          current: DEFAULT_PAGE_NUMBER,
        },
      };
    case 'SET_PAGINATION':
      return {
        ...state,
        pagination: action.payload,
      };
    case 'SET_SORT':
      return {
        ...state,
        sort: action.payload as customersPageState['sort'],
      };
  }
};
const serializeFilters = (filters: customersPageState['filters']): string | undefined => {
  const parts: string[] = [];
  if (filters?.query) parts.push(`name=ini='*${filters.query}*'`);
  if (filters?.showInActive !== undefined) parts.push(`isInactive=='${filters?.showInActive}'`);

  return parts.length ? parts.join(';') : undefined;
};

const serializeSort = (sort: customersPageState['sort']): string | undefined => {
  if (sort.field && sort.order) {
    const field = Array.isArray(sort.field) ? sort.field.join(',') : sort.field.toString();
    return `${sort.order === 'descend' ? '-' : ''}${field}`;
  }
  return undefined;
};
const CustomersPage = (): ReactNode => {
  const { session } = useContext(AuthContext);
  const notification = useNotification();
  const { token } = theme.useToken();
  const [editedCustomer, setEditedCustomer] = useState<Customer>();
  const [state, setState] = useReducer(reducer, {
    filters: {
      query: '',
      showInActive: session?.claims.role === Roles.ROLE_HHC_ADMIN ? false : undefined,
    },
    pagination: { current: DEFAULT_PAGE_NUMBER, pageSize: DEFAULT_PAGE_SIZE },
    sort: { field: DEFAULT_SORT_FIELD, order: DEFAULT_SORT_DIRECTION },
  });
  const [debouncedState] = useDebouncedState({ state, time: 1000 });
  const customers = useQuery<Customer[]>(
    debouncedState && [
      'customer',
      {
        filter: serializeFilters(debouncedState.filters),
        sort: serializeSort(debouncedState.sort),
        fields: {
          customer: ['name', 'contactEmail', 'contactPhone', 'isInactive'],
        },
        page: {
          totals: 1,
          number: debouncedState.pagination.current,
          size: debouncedState.pagination.pageSize,
        },
      },
    ]
  );
  useEffect(() => {
    if (editedCustomer?.id) {
      setEditedCustomer(customers.data?.find((c) => c.id === editedCustomer.id));
    }
  }, customers.data);

  const columns: TableColumnsType<Customer> = [
    { title: '', width: '50px' },
    {
      title: 'Name',
      dataIndex: ['name'],
      sorter: true,
      sortDirections: ['ascend', 'descend', 'ascend'],
      defaultSortOrder: DEFAULT_SORT_DIRECTION,
      render: (value, item) => (
        <Typography.Link
          onClick={() => {
            setEditedCustomer(item);
          }}
        >
          {value}
        </Typography.Link>
      ),
    },
    {
      title: 'Email',
      dataIndex: ['contactEmail'],
      sorter: true,
      sortDirections: ['ascend', 'descend', 'ascend'],
      defaultSortOrder: DEFAULT_SORT_DIRECTION,
    },
    {
      title: 'Contact Phone',
      dataIndex: ['contactPhone'],
      sorter: true,
      sortDirections: ['ascend', 'descend', 'ascend'],
      defaultSortOrder: DEFAULT_SORT_DIRECTION,
    },
    {
      title: 'Status',
      dataIndex: ['isInactive'],
      render: (isInActive) =>
        !isInActive ? (
          <Flex gap="small" align="center">
            <CircleCheck style={{ color: token.colorTextDisabled }} />
            <Typography.Text>Active</Typography.Text>
          </Flex>
        ) : (
          <Flex gap="small" align="center">
            <CircleX style={{ color: token.colorTextDisabled }} />
            <Typography.Text>In active</Typography.Text>
          </Flex>
        ),
      sorter: true,
      sortDirections: ['ascend', 'descend', 'ascend'],
      defaultSortOrder: DEFAULT_SORT_DIRECTION,
    },
  ];

  const handleTableChange = (pagination: any, f: any, sorter: any): void => {
    setState({
      type: 'SET_PAGINATION',
      payload: { current: pagination.current, pageSize: pagination.pageSize },
    });
    setState({ type: 'SET_SORT', payload: Array.isArray(sorter) ? sorter[0] : sorter });
  };

  return (
    <Flex
      gap={token.marginMD}
      style={{
        height: 'calc(100vh - 70px)',
      }}
      vertical
    >
      <Layout
        style={{
          paddingBlockEnd: token.paddingMD,
        }}
      >
        <Layout.Content>
          <AuthorizedComponent roles={[Roles.ROLE_HHC_ADMIN]}>
            <DataTable<Customer>
              title="Customers"
              description="List of customers"
              filtersNode={
                <CustomersFilter
                  initialFilters={state.filters}
                  onChange={(filters) => {
                    setState({ type: 'SET_FILTER', payload: filters });
                  }}
                />
              }
              actionsNode={
                <Button
                  type="primary"
                  icon={<PlusCircle />}
                  onClick={() => {
                    setEditedCustomer(new Customer());
                  }}
                >
                  Add Customer
                </Button>
              }
              dataList={customers}
              onChange={setState}
              currentPage={state.pagination.current}
              columns={columns}
              actions={
                session?.claims.role === Roles.ROLE_HHC_ADMIN
                  ? [
                      {
                        key: 'delete',
                        label: 'Delete',
                        icon: <Trash2 />,
                        danger: true,
                        onItemClick: (record) => {
                          Modal.confirm({
                            width: '50%',
                            title: 'Confirm deletion',
                            icon: <CircleAlert />,
                            content: (
                              <Typography.Text>
                                Are you sure you want to delete <b>{record.name}</b> permanently?
                              </Typography.Text>
                            ),
                            okText: 'Yes',
                            okType: 'danger',
                            cancelText: 'No',
                            onOk: () => {
                              from(customers.client.delete(['customer', record.id]))
                                .pipe(
                                  switchMap((res) =>
                                    res.error
                                      ? throwError(
                                          () => new Error(res.error?.detail ?? res.error?.title)
                                        )
                                      : of(res.data)
                                  ),
                                  tap(() => {
                                    notification.success({
                                      message: ' ',
                                      description: SUCCESS_DELETE_MESSAGE,
                                    });
                                    customers.refetch?.();
                                  }),
                                  catchError((error: Error) => {
                                    notification.error({
                                      message: 'Failed to delete customer',
                                      description: error.message,
                                    });
                                    return EMPTY;
                                  })
                                )
                                .subscribe();
                            },
                          });
                        },
                      },
                    ]
                  : undefined
              }
            />
          </AuthorizedComponent>
          <AuthorizedComponent roles={[Roles.ROLE_CUSTOMER_ADMIN]}>
            <Suspense fallback={<LoadingSpin />}>
              <CustomerFormTabsComponent
                customer={customers.data?.[0] ?? new Customer()}
                onConfirm={() => {
                  customers.refetch?.();
                }}
                onCancel={() => {}}
              />
            </Suspense>
          </AuthorizedComponent>
        </Layout.Content>
        <AuthorizedComponent roles={[Roles.ROLE_HHC_ADMIN]}>
          <SyncSuspense condition={editedCustomer}>
            <ExtraContent>
              <CustomerFormTabsComponent
                customer={editedCustomer ?? new Customer()}
                onConfirm={(customer: Customer, close: boolean) => {
                  customers.refetch?.();
                  if (close) setEditedCustomer(undefined);
                  else
                    setEditedCustomer({
                      ...editedCustomer,
                      ...customer,
                      id: editedCustomer?.id ?? customer.id,
                    });
                }}
                onCancel={() => {
                  setEditedCustomer(undefined);
                }}
              />
            </ExtraContent>
          </SyncSuspense>
        </AuthorizedComponent>
      </Layout>
    </Flex>
  );
};

CustomersPage.propTypes = {
  defaultPageNumber: PropTypes.number,
  defaultPageSize: PropTypes.number,
};

CustomersPage.defaultProps = {
  defaultPageNumber: 1,
  defaultPageSize: 10,
  onVisibleColumnsChange: () => {},
  onSelectRowsChange: () => {},
};

CustomersPage.displayName = 'CustomersPage';
export default CustomersPage;
